import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { createThemedStyles } from '../theme/useThemedStyles';
import { useGoals } from '../context/GoalsContext';
import { useIncome } from '../context/IncomeContext';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency, parseCurrencyInput } from '../utils/currency';
import { IncomeFrequency } from '../types/income';
import AdBanner from '../components/AdBanner';
import { useSubscription } from '../context/SubscriptionContext';
import { useInterstitialAd } from '../hooks/useInterstitialAd';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';
import AppLoadingScreen from '../components/AppLoadingScreen';

const calculateMonthlyIncome = (amount: number, frequency: IncomeFrequency): number => {
  switch (frequency) {
    case 'bi-weekly':
      return Math.round((amount * 26) / 12); // 26 pay periods per year
    case 'weekly':
      return Math.round((amount * 52) / 12); // 52 weeks per year
    case 'daily':
      return Math.round(amount * 30); // Approximate 30 days per month
    case 'monthly':
    default:
      return amount;
  }
};

const IncomeScreen = () => {
  const { goals, updateGoal, updateIncome: updateGoalsIncome } = useGoals();
  const { income: incomeContext, updateIncome: updateIncomeContext, isInitialized } = useIncome();
  const { settings } = useSettings();
  const [editedIncome, setEditedIncome] = useState(incomeContext.amount.toString());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { subscription } = useSubscription();
  const styles = useIncomeStyles();

  // Show interstitial ad when screen comes into focus (rate limited to every 10 minutes)
  useInterstitialAd();

  // Debug log for context value
  console.log('IncomeScreen: incomeContext value:', incomeContext);
  console.log('IncomeScreen: monthlyIncome value:', incomeContext.monthlyIncome);
  console.log('IncomeScreen: settings.currency:', settings.currency);

  // Update local state when income changes
  useEffect(() => {
    console.log('IncomeScreen: Updating editedIncome from incomeContext.amount:', incomeContext.amount);
    setEditedIncome(incomeContext.amount.toString());
  }, [incomeContext.amount]);

  // Calculate monthly income for display (fallback to 0 if undefined)
  const displayMonthlyIncome = incomeContext.monthlyIncome || 0;

  const handleIncomeUpdate = async () => {
    try {
      setError(null);
      setSuccessMessage(null);
      const amount = parseCurrencyInput(editedIncome);
      
      if (editedIncome.trim() === '' || isNaN(amount) || amount < 0) {
        setError('Please enter a valid income amount greater than 0');
        return;
      }

      console.log('Starting income update with amount:', amount);
      console.log('Current settings frequency:', settings.income.frequency);

      const newMonthlyIncome = calculateMonthlyIncome(amount, settings.income.frequency);
      console.log('Calculated new monthly income:', newMonthlyIncome);

      const totalAllocated = goals.reduce((sum, goal) => sum + goal.allocatedPercentage, 0);
      console.log('Total allocated percentage:', totalAllocated);

      const unallocatedAmount = Math.round((newMonthlyIncome * (100 - totalAllocated)) / 100);
      console.log('Calculated unallocated amount:', unallocatedAmount);

      // Show loading state
      setIsLoading(true);

      const updatedIncome = {
        amount,
        frequency: settings.income.frequency,
        monthlyIncome: newMonthlyIncome,
        unallocatedAmount
      };

      console.log('Attempting to update income with:', updatedIncome);

      try {
        // Update both contexts sequentially to avoid race conditions
        console.log('Updating goals context...');
        await updateGoalsIncome(updatedIncome);
        
        console.log('Updating income context...');
        await updateIncomeContext(updatedIncome);
        
        console.log('Both contexts updated successfully');
        
        setSuccessMessage('Income updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error('Error during context updates:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in handleIncomeUpdate:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update income';
      setError(errorMessage);
      Alert.alert('Error', `Failed to update income: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateDeadline = (currentAmount: number, targetAmount: number, monthlyContribution: number) => {
    if (!monthlyContribution || monthlyContribution <= 0) return '';
    const remainingAmount = targetAmount - currentAmount;
    const monthsNeeded = Math.ceil(remainingAmount / monthlyContribution);
    const deadlineDate = new Date();
    deadlineDate.setMonth(deadlineDate.getMonth() + monthsNeeded);
    return deadlineDate.toISOString().split('T')[0];
  };

  const getFrequencyLabel = () => {
    switch (settings.income.frequency) {
      case 'bi-weekly':
        return 'Bi-Weekly Income';
      case 'weekly':
        return 'Weekly Income';
      case 'daily':
        return 'Daily Income';
      default:
        return 'Monthly Income';
    }
  };

  const handleAllocationUpdate = async (goalId: string, newAllocation: string) => {
    try {
      setError(null);
      const allocation = parseFloat(newAllocation);
      
      if (isNaN(allocation) || allocation < 0 || allocation > 100) {
        setError('Please enter a percentage between 0 and 100');
        Alert.alert('Invalid Allocation', 'Please enter a percentage between 0 and 100');
        return;
      }

      const goal = goals.find(g => g.id === goalId);
      if (!goal) {
        setError('Goal not found');
        return;
      }

      const otherGoalsAllocation = goals
        .filter(g => g.id !== goalId)
        .reduce((sum, g) => sum + g.allocatedPercentage, 0);

      const totalAllocation = otherGoalsAllocation + allocation;
      if (totalAllocation > 100) {
        setError('Total allocation cannot exceed 100%');
        Alert.alert('Invalid Allocation', 'Total allocation cannot exceed 100%');
        return;
      }

      setIsLoading(true);

      try {
        // Calculate new monthly contribution based on allocation
        const newMonthlyContribution = Math.round((incomeContext.monthlyIncome * allocation) / 100);

        // Calculate new deadline based on new monthly contribution
        const newDeadline = calculateDeadline(
          goal.currentAmount,
          goal.targetAmount,
          newMonthlyContribution
        );

        // Update goal with new allocation, monthly contribution, and deadline
        const updatedGoal = {
          ...goal,
          allocatedPercentage: allocation,
          monthlyContribution: newMonthlyContribution,
          deadline: newDeadline,
          lastUpdated: new Date().toISOString()
        };

        // First update the goal
        await updateGoal(updatedGoal);

        // Then update unallocated amount in income
        const newUnallocatedAmount = Math.round((incomeContext.monthlyIncome * (100 - totalAllocation)) / 100);
        await updateIncomeContext({
          ...incomeContext,
          unallocatedAmount: newUnallocatedAmount
        });

      } catch (error) {
        console.error('Error updating goal allocation:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in handleAllocationUpdate:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update allocation';
      setError(errorMessage);
      Alert.alert('Error', 'Failed to update allocation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isInitialized) {
    return <AppLoadingScreen showBrand={false} />;
  }

  return (
    <WebPage title="Income & Allocation">
      <PageScroll style={styles.container}>
        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}
        
        <View style={styles.section}>
          <Text style={styles.title}>{getFrequencyLabel()}</Text>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Amount</Text>
            <TextInput
              style={[styles.input, isLoading && styles.inputDisabled]}
              value={editedIncome}
              onChangeText={text => {
                // Allow empty string, but do not update with NaN
                if (/^\d*$/.test(text) || text === '') {
                  setEditedIncome(text);
                }
              }}
              keyboardType="numeric"
              placeholder={`Enter your ${settings.income.frequency.toLowerCase()} income`}
              editable={!isLoading}
            />

            <TouchableOpacity
              style={[styles.updateButton, isLoading && styles.updateButtonDisabled]}
              onPress={handleIncomeUpdate}
              disabled={isLoading}
            >
              <Text style={styles.updateButtonText}>
                {isLoading ? 'Updating...' : 'Update Income'}
              </Text>
            </TouchableOpacity>

            <View style={styles.summaryContainer}>
              <Text style={styles.summaryTitle}>Monthly Equivalent</Text>
              <Text style={styles.summaryAmount}>
                {formatCurrency(displayMonthlyIncome, settings.currency.code, settings.currency.symbol)}
              </Text>
            </View>

            {successMessage && <Text style={styles.successText}>{successMessage}</Text>}
          </View>

          <View style={styles.allocationSection}>
            <Text style={styles.sectionTitle}>Goal Allocations</Text>
            {goals.map(goal => (
              <View key={goal.id} style={styles.goalAllocation}>
                <View style={styles.goalInfo}>
                  <Text style={styles.goalName}>{goal.name}</Text>
                  <Text style={styles.goalType}>{goal.type}</Text>
                </View>
                <View style={styles.allocationInput}>
                  <TextInput
                    style={[styles.percentageInput, isLoading && styles.inputDisabled]}
                    value={goal.allocatedPercentage?.toFixed(2) || '0.00'}
                    onChangeText={(text) => handleAllocationUpdate(goal.id, text)}
                    keyboardType="numeric"
                    placeholder="0"
                    editable={!isLoading}
                  />
                  <Text style={styles.percentageSymbol}>%</Text>
                </View>
                <Text style={styles.monthlyAmount}>
                  {formatCurrency(
                    (displayMonthlyIncome * goal.allocatedPercentage) / 100,
                    settings.currency.code,
                    settings.currency.symbol
                  )}
                </Text>
              </View>
            ))}

            <View style={styles.unallocated}>
              <Text style={styles.unallocatedText}>Unallocated</Text>
              <Text style={styles.unallocatedAmount}>
                {formatCurrency(
                  incomeContext.unallocatedAmount || 0,
                  settings.currency.code,
                  settings.currency.symbol
                )}
              </Text>
            </View>
          </View>
        </View>
      </PageScroll>

      {/* Ad Banner - continuously shown for non-subscribers */}
      <AdBanner />
    </WebPage>
  );
};

const useIncomeStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background.default,
  },
  section: {
    ...(Platform.OS === 'web'
      ? { paddingVertical: 20, paddingHorizontal: 0 }
      : { padding: 20 }),
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    color: colors.text.secondary,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: colors.text.primary,
  },
  updateButton: {
    backgroundColor: colors.button.primary,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 20,
  },
  updateButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  summaryContainer: {
    backgroundColor: colors.background.paper,
    padding: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  summaryTitle: {
    fontSize: 16,
    color: colors.text.secondary,
    marginBottom: 8,
  },
  summaryAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  allocationSection: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 16,
  },
  goalAllocation: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  goalInfo: {
    flex: 2,
  },
  goalName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  goalType: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  allocationInput: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginHorizontal: 16,
  },
  percentageInput: {
    width: 50,
    textAlign: 'right',
    fontSize: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[300],
  },
  percentageSymbol: {
    fontSize: 16,
    color: colors.text.secondary,
    marginLeft: 4,
  },
  monthlyAmount: {
    flex: 1,
    textAlign: 'right',
    fontSize: 16,
    color: colors.text.primary,
  },
  unallocated: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.grey[300],
  },
  unallocatedText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  unallocatedAmount: {
    fontSize: 16,
    color: colors.text.secondary,
  },
  errorContainer: {
    backgroundColor: colors.error.light,
    padding: 12,
    marginHorizontal: Platform.OS === 'web' ? 0 : 20,
    marginTop: 20,
    borderRadius: 8,
  },
  errorText: {
    color: colors.error.dark,
    fontSize: 14,
  },
  inputDisabled: {
    opacity: 0.7,
  },
  updateButtonDisabled: {
    backgroundColor: colors.grey[400],
  },
  successText: {
    color: colors.success.main,
    fontSize: 14,
    marginTop: 12,
  },
}));

export default IncomeScreen;